import { useState } from 'react';
import SharedDialog from './shared-dialog';

import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

interface DialogExampleProps {
  open: boolean;
  handleClose: () => void;
  onConfirm: () => Promise<void>;
  actingAs?: string;
}

export function CreateOrgUnitDialogExample({
  open,
  handleClose,
  onConfirm,
  actingAs,
}: DialogExampleProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [parentOrgUnitPath, setParentOrgUnitPath] = useState('/');

  const handleCloseAndReset = () => {
    setName('');
    setDescription('');
    setParentOrgUnitPath('/');
    handleClose();
  };

  const handleConfirm = async () => {
    setIsLoading(true);
    try {
      await onConfirm();
      handleCloseAndReset();
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <SharedDialog
      open={open}
      handleClose={handleCloseAndReset}
      title="Create Organizational Unit"
      actingAs={actingAs}
      dialogContent={
        <Stack gap={1}>
          <TextField
            label="Organizational Unit"
            fullWidth
            required
            autoFocus
            autoComplete="off"
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={!name}
            helperText={!name ? 'Name is required.' : ' '}
          />
          <TextField
            label="Description"
            fullWidth
            autoComplete="off"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Parent Organization Unit</InputLabel>
            <Select
              label="Parent Organization Unit"
              value={parentOrgUnitPath}
              onChange={(e) => setParentOrgUnitPath(e.target.value)}
            >
              <MenuItem value="/">/</MenuItem>
              <MenuItem value="/Sales">/Sales</MenuItem>
              <MenuItem value="/Engineering">/Engineering</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      }
      primaryButton={{
        title: 'Create Org Unit',
        action: handleConfirm,
        loading: isLoading,
        disabled: isLoading || !name,
      }}
    />
  );
}

export function EditOrgUnitDialogExample({
  open,
  handleClose,
  onConfirm,
}: DialogExampleProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [name, setName] = useState('Engineering');
  const [description, setDescription] = useState('Engineering department');
  const [parentOrgUnitPath, setParentOrgUnitPath] = useState('/');

  const handleConfirm = async () => {
    setIsLoading(true);
    try {
      await onConfirm();
      handleClose();
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <SharedDialog
      open={open}
      handleClose={handleClose}
      title="Edit Organizational Unit"
      dialogContent={
        <Stack gap={1}>
          <TextField
            label="Organizational Unit"
            fullWidth
            required
            autoFocus
            autoComplete="off"
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={!name}
            helperText={!name ? 'Name is required.' : ' '}
          />
          <TextField
            label="Description"
            fullWidth
            autoComplete="off"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Parent Organization Unit</InputLabel>
            <Select
              label="Parent Organization Unit"
              value={parentOrgUnitPath}
              onChange={(e) => setParentOrgUnitPath(e.target.value)}
            >
              <MenuItem value="/">/</MenuItem>
              <MenuItem value="/Sales">/Sales</MenuItem>
              <MenuItem value="/Engineering">/Engineering</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      }
      primaryButton={{
        title: 'Save Changes',
        action: handleConfirm,
        loading: isLoading,
        disabled: isLoading || !name,
      }}
    />
  );
}

export function SetOrgUnitForUsersDialogExample({
  open,
  handleClose,
  onConfirm,
}: DialogExampleProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [orgUnitPath, setOrgUnitPath] = useState('');
  const count = 2;

  const handleCloseAndReset = () => {
    setOrgUnitPath('');
    handleClose();
  };

  const handleConfirm = async () => {
    setIsLoading(true);
    try {
      await onConfirm();
      handleCloseAndReset();
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <SharedDialog
      open={open}
      handleClose={handleCloseAndReset}
      title="Set Organizational Unit"
      dialogContent={
        <Stack gap={2}>
          <Typography variant="body1">
            Set organizational unit for <strong>{count} users</strong>.
          </Typography>
          <TextField
            label="Organizational Unit Path"
            fullWidth
            autoFocus
            autoComplete="off"
            placeholder="e.g., /Sales/West Coast"
            value={orgUnitPath}
            onChange={(e) => setOrgUnitPath(e.target.value)}
          />
        </Stack>
      }
      primaryButton={{
        title: 'Set Org Unit',
        action: handleConfirm,
        loading: isLoading,
        disabled: isLoading || !orgUnitPath,
      }}
    />
  );
}

export function DeleteOrgUnitDialogExample({
  open,
  handleClose,
  onConfirm,
}: DialogExampleProps) {
  const [isLoading, setIsLoading] = useState(false);
  const orgUnitName = 'Old Marketing';

  const handleConfirm = async () => {
    setIsLoading(true);
    try {
      await onConfirm();
      handleClose();
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <SharedDialog
      open={open}
      handleClose={handleClose}
      title="Delete Organizational Unit"
      dialogContent={
        <Typography variant="body1">
          Delete <strong>{orgUnitName}</strong>?
        </Typography>
      }
      primaryButton={{
        title: 'Delete Organizational Unit',
        color: 'error',
        action: handleConfirm,
        loading: isLoading,
        disabled: isLoading,
      }}
    />
  );
}

export function MoveToTrashDialogExample({
  open,
  handleClose,
  onConfirm,
}: DialogExampleProps) {
  const [isLoading, setIsLoading] = useState(false);
  const itemName = 'bq-results-20240216-161104-1708099992462';
  const actingAs = 'useremail@dev.promevo.com';

  const handleConfirm = async () => {
    setIsLoading(true);
    try {
      await onConfirm();
      handleClose();
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <SharedDialog
      open={open}
      handleClose={handleClose}
      title="Move to Trash"
      actingAs={actingAs}
      dialogContent={
        <Typography variant="body1">
          Move <strong>{itemName}</strong> to trash?
        </Typography>
      }
      primaryButton={{
        title: 'Move to Trash',
        color: 'warning',
        action: handleConfirm,
        loading: isLoading,
        disabled: isLoading,
      }}
    />
  );
}

export function MoveMultipleToTrashDialogExample({
  open,
  handleClose,
  onConfirm,
}: DialogExampleProps) {
  const [isLoading, setIsLoading] = useState(false);
  const selectedCount = 2;
  const actingAs = 'useremail@dev.promevo.com';

  const handleConfirm = async () => {
    setIsLoading(true);
    try {
      await onConfirm();
      handleClose();
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <SharedDialog
      open={open}
      handleClose={handleClose}
      title="Move to Trash"
      actingAs={actingAs}
      dialogContent={
        <Typography variant="body1">
          Move <strong>{selectedCount} selected items</strong> to trash?
        </Typography>
      }
      primaryButton={{
        title: 'Move to Trash',
        color: 'warning',
        action: handleConfirm,
        loading: isLoading,
        disabled: isLoading,
      }}
    />
  );
}
